import { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const ROLE_LABELS = {
  1: { label: 'Viewer',    color: 'var(--text-muted)'  },
  2: { label: 'Operator',  color: 'var(--yellow)'       },
  3: { label: 'Manager',   color: 'var(--accent)'       },
  4: { label: 'Admin',     color: 'var(--green)'        },
};

const NAV = [
  { to: '/dashboard',    icon: '▦', label: 'Dashboard',    minLevel: 1 },
  { to: '/transactions', icon: '⇄', label: 'Transactions', minLevel: 2 },
  { to: '/reports',      icon: '◉', label: 'Reports',      minLevel: 2 },
  { to: '/mailboxes',    icon: '⊟', label: 'Mailboxes',    minLevel: 3 },
  { to: '/groups',       icon: '◈', label: 'Groups',       minLevel: 3 },
  { to: '/services',     icon: '⚙', label: 'Services',     minLevel: 3 },
  { to: '/users',        icon: '⊕', label: 'Users',        minLevel: 3 },
];

const MAINTENANCE = [
  { to: '/maintenance/scheme-codes', icon: '⊞', label: 'Scheme Codes', minLevel: 3 },
  { to: '/maintenance/ipbm-rate',    icon: '₹', label: 'IPBM Rate',    minLevel: 3 },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [maintOpen, setMaintOpen] = useState(
    MAINTENANCE.some(m => location.pathname.startsWith(m.to))
  );

  const handleLogout = () => { logout(); navigate('/login'); };

  const level = user?.accesslevel ?? 0;
  const role  = ROLE_LABELS[level] || { label: `Level ${level}`, color: 'var(--text-muted)' };
  const visibleNav  = NAV.filter(n => level >= n.minLevel);
  const visibleMaint= MAINTENANCE.filter(n => level >= n.minLevel);

  const navLinkStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 12px',
    borderRadius: 6,
    textDecoration: 'none',
    color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
    background: isActive ? 'var(--accent-dim)' : 'transparent',
    borderLeft: isActive ? '2px solid var(--accent)' : '2px solid transparent',
    fontWeight: isActive ? 700 : 500,
    fontSize: 13,
    marginBottom: 2,
    transition: 'var(--transition)',
  });

  return (
    <aside style={{
      width: 220, minHeight: '100vh',
      background: 'var(--bg-panel)',
      borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column', flexShrink: 0,
    }}>
      {/* Logo */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/icon.png" alt="SmartSwitch icon"
            style={{ width: 34, height: 34, objectFit: 'contain', flexShrink: 0 }} />
          <div style={{ background: 'white', borderRadius: 6, padding: '3px 8px', lineHeight: 0 }}>
            <img src="/logo.png" alt="SmartSwitch"
              style={{ height: 22, objectFit: 'contain', display: 'block' }} />
          </div>
        </div>
      </div>

      {/* Instance badge */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{
          background: 'var(--accent-dim)', border: '1px solid rgba(0,212,255,0.2)',
          borderRadius: 4, padding: '5px 10px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Instance</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
            {user?.instance || 'N/A'}
          </span>
        </div>
      </div>

      {/* Main nav */}
      <nav style={{ flex: 1, padding: '10px 10px' }}>
        {visibleNav.map(n => (
          <NavLink key={n.to} to={n.to} style={({ isActive }) => navLinkStyle(isActive)}>
            <span style={{ fontSize: 15, width: 18, textAlign: 'center' }}>{n.icon}</span>
            {n.label}
          </NavLink>
        ))}

        {/* Maintenance group */}
        {visibleMaint.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => setMaintOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', padding: '7px 12px',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: 11,
                fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                borderRadius: 6,
              }}
            >
              <span>Maintenance</span>
              <span style={{ fontSize: 9, transition: 'transform 0.2s', transform: maintOpen ? 'rotate(90deg)' : 'none' }}>▶</span>
            </button>

            {maintOpen && visibleMaint.map(n => (
              <NavLink key={n.to} to={n.to} style={({ isActive }) => ({
                ...navLinkStyle(isActive),
                paddingLeft: 28,
              })}>
                <span style={{ fontSize: 14, width: 18, textAlign: 'center' }}>{n.icon}</span>
                {n.label}
              </NavLink>
            ))}
          </div>
        )}
      </nav>

      {/* User info + logout */}
      <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)' }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.name || user?.userid}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
            <span style={{ fontSize: 11, color: role.color, fontWeight: 700 }}>{role.label}</span>
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>· L{level}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <button className="btn btn-ghost"
            style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            {theme === 'dark' ? '☀ Light' : '☾ Dark'}
          </button>
          <button className="btn btn-ghost"
            style={{ flex: 1, justifyContent: 'center', fontSize: 12 }}
            onClick={handleLogout}>
            ⏻ Sign Out
          </button>
        </div>
      </div>
    </aside>
  );
}
